import React, { useEffect, useRef, useState } from "react";
import { renderToString } from "react-dom/server";
import mapboxgl from "!mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Link as GatsbyLink } from "gatsby";
import { trackOutboundLink } from "../../../services/gtm";
import { useSearchResultsStore } from "../../../services/store";
import { isExternalLink } from "../../../helpers";

mapboxgl.accessToken = process.env.GATSBY_MAPBOX_API_KEY;

const mapIcons = [
  {
    name: "fallback",
    url:
      "https://rob-cdn.ams3.digitaloceanspaces.com/website/general/map-marker.png",
  },
  {
    name: "individual_inns",
    url:
      "https://rob-cdn.ams3.digitaloceanspaces.com/website/general/map_pins/map-icon-individual_inns.png",
  },
  {
    name: "managed",
    url:
      "https://rob-cdn.ams3.digitaloceanspaces.com/website/general/map_pins/map-icon-managed.png",
  },
  {
    name: "tenanted",
    url:
      "https://rob-cdn.ams3.digitaloceanspaces.com/website/general/map_pins/map-icon-tenanted.png",
  },
];

function pubsToMapFeatures(pubs, props) {
  let mapFeatures = [];
  if (!pubs) {
    return mapFeatures;
  }

  pubs.map(pub => {
    const pubData = pub?.PubData ?? pub;
    let link = false;
    if (pubData.hasOwnWebsite || isExternalLink(pubData.url)) {
      link = (
        <a href={pubData.url} target="_blank" onClick={() => trackOutboundLink(pubData.url)}>
          View Pub
        </a>
      );
    } else {
      link = (
        <GatsbyLink to={pubData.url} onClick={() => trackOutboundLink(pub.url)}>
          View Pub
        </GatsbyLink>
      );
    }
    link = renderToString(link);

    const town = pubData.address2 ? `<p class='town'>${pubData.address2}</p>` : "";

    mapFeatures.push({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [pubData.longitude, pubData.latitude],
      },
      properties: {
        name: pubData.name,
        town: town,
        directionsUrl: 'https://www.google.com/maps/dir/?api=1&destination=' + (pubData.googlePlaceId ? `${encodeURI(pubData.name)}&destination_place_id=${pubData.googlePlaceId}` : `${pubData.latitude},${pubData.longitude}`),
        link: link,
        featuredImage: pubData.url_featured_image,
        icon:
          pubData.brand === "Individual Inns"
            ? "individual_inns"
            : pubData.brand === "Managed"
            ? "managed"
            : pubData.brand === "Robinsons"
            ? "tenanted"
            : "fallback",
      },
    });
  });
  return mapFeatures;
}

function Map(props) {
  const [map, setMap] = useState(false);
  const mapContainer = useRef(null);
  const {
    searchResults,
    searchLocation,
    setAllPubs,
  } = useSearchResultsStore();
  const [popups, setPopups] = useState([]);
  const defaultCenter = [-2.6, 53.5];
  const defaultZoom = 7;
  const showAll = props.showAll ?? false;

  const getBoundsArray = () => {
    const bounds = {
      low_lat: 90,
      high_lat: -90,
      low_lng: 180,
      high_lng: -180
    }
    let lng, lat;
    for (let i = 0; i < searchResults.length;  i++) {
      lng = searchResults[i].PubData.longitude;
      lat = searchResults[i].PubData.latitude;
      if (lng < bounds.low_lng) {
        bounds.low_lng = lng
      }
      if (lng > bounds.high_lng) {
        bounds.high_lng = lng
      }
      if (lat < bounds.low_lat) {
        bounds.low_lat = lat
      }
      if (lat > bounds.high_lat) {
        bounds.high_lat = lat
      }
    }
    const boundsArray = [
      [bounds.low_lng, bounds.low_lat],
      [bounds.high_lng, bounds.high_lat]
    ]
    return boundsArray;
  }

  const afterSourceLoaded = (callback) => {
    (function checkSource() {
      const source = map.getSource("points");
      if (typeof source === "undefined") {
        setTimeout(() => {
          checkSource();
        }, 1)
      } else {
        callback(source);
      }
    })();
  }

  useEffect(() => {
    // Close all popups when some state (e.g., props.pubs) changes
    popups.forEach(popup => popup.remove());
    setPopups([]); // Clear popups from state

    setAllPubs(props.pubs);
  }, [props.pubs]);

  useEffect(() => {
    setAllPubs(props.pubs);
  }, []);

  useEffect(() => {
    if (!map) {
      initMap({ setMap, mapContainer });
    }
  }, [map]);

  useEffect(() => {
    updateMapMarkers();
  }, [searchResults, map]);

  const initMap = ({ setMap, mapContainer }) => {
    const map = new mapboxgl.Map({
      // eslint-disable-line
      container: mapContainer.current,
      style: "mapbox://styles/brew-admin/clk0vqq2i001x01pgdhibawkg",
      center: defaultCenter,
      zoom: defaultZoom,
    });

    // Add zoom and rotation controls to the map.
    map.addControl(new mapboxgl.NavigationControl());

    map.on("load", function () {
      mapIcons.forEach(icon => {
        map.loadImage(icon.url, function (error, image) {
          if (error) throw error;
          map.addImage(icon.name, image);
        });
      });
      map.loadImage(mapIcons[0].url, function (error, image) {
        if (error) throw error;
        // map.addImage("marker", image);
        const mapFeatures = pubsToMapFeatures(showAll ? props.pubs : searchResults);
        map.addSource("points", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: mapFeatures,
          },
          cluster: true,
        });

        // Map clusters
        map.addLayer({
          id: 'clusters',
          type: 'circle',
          source: 'points',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': '#000000',
            'circle-radius': [
              'step',
              ['get', 'point_count'],
              20,
              100,
              30,
              750,
              40
            ]
          }
        });
        map.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'points',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 16
          },
          paint: {
            'text-color': '#B35F3A'
          }
        });
        // inspect a cluster on click
        map.on('click', 'clusters', (e) => {
          const features = map.queryRenderedFeatures(e.point, {
            layers: ['clusters']
          });
          const clusterId = features[0].properties.cluster_id;
          map.getSource('points').getClusterExpansionZoom(
            clusterId,
            (err, zoom) => {
              if (err) return;
              map.easeTo({
                center: features[0].geometry.coordinates,
                zoom: zoom
              });
            }
          );
        });
        map.on('mouseenter', 'clusters', () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', 'clusters', () => {
          map.getCanvas().style.cursor = '';
        });

        map.addLayer({
          id: "points",
          type: "symbol",
          source: "points",
          layout: {
            "icon-image": ["get", "icon"],
            "icon-size": 1,
            "icon-anchor": "bottom",
          },
        });
        map.on("click", "points", e => {
          const coordinates = e.features[0].geometry.coordinates.slice();

          // Ensure that if the map is zoomed out such that multiple
          // copies of the feature are visible, the popup appears
          // over the copy being pointed to.
          while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
          }

          const popup = new mapboxgl.Popup({ offset: 32 })
            .setLngLat(coordinates)
            .setHTML(
              `
              <img src="${e.features[0].properties.featuredImage}" alt="${e.features[0].properties.name}" width="280" />
              <h3>${e.features[0].properties.name}</h3>
              ${e.features[0].properties.town}
              <a target="_blank" href="${e.features[0].properties.directionsUrl}">Get Directions</a>
              ${e.features[0].properties.link}
            `
            )
            .addTo(map);

          setPopups(prevPopups => [...prevPopups, popup]);
        });

        map.on("mouseenter", "points", () => {
          map.getCanvas().style.cursor = "pointer";
        });

        // Change it back to a pointer when it leaves.
        map.on("mouseleave", "points", () => {
          map.getCanvas().style.cursor = "";
        });

      });
      setMap(map);
    });
  };

  const updateMapMarkers = () => {
    if (map) {
      if (searchResults.length === 1) {
        map.flyTo({
          center: [searchResults[0].PubData.longitude, searchResults[0].PubData.latitude],
          zoom: 13,
        });
        afterSourceLoaded((source) => {
          source.setData({
            type: "FeatureCollection",
            features: pubsToMapFeatures(searchResults),
          });
        });
      } else if (searchResults.length > 1) {
        afterSourceLoaded((source) => {
          source.setData({
            type: "FeatureCollection",
            features: pubsToMapFeatures(searchResults),
          });

          const zoomOutUntilVisible = () => {
            const mapBounds = map.getBounds();
            const pubsWithinBounds = searchResults.some((pub) => {
              return mapBounds.contains([pub.PubData.longitude, pub.PubData.latitude]);
            });
            if (!pubsWithinBounds) {
              map.once('zoomend', zoomOutUntilVisible);
              map.zoomOut();
            }
          }

          const atLeastOneResultByLocation = searchResults.some((pub) => {
            return pub.hasOwnProperty('distance')
          });
          if (atLeastOneResultByLocation && searchLocation?.longitude && searchLocation?.latitude) {
            map.once('moveend', zoomOutUntilVisible);
            map.flyTo({
              center: [searchLocation.longitude, searchLocation.latitude],
              zoom: 12,
            });
          } else {
            const boundsArray = getBoundsArray();
            map.fitBounds(boundsArray, {
              padding: 50
            });
          }
        });
      } else {
        // searchResults.length === 0
        if (searchLocation?.longitude && searchLocation?.latitude) {
          map.flyTo({
            center: [searchLocation.longitude, searchLocation.latitude],
            zoom: 9,
          });
        } else {
          map.flyTo({
            center: defaultCenter,
            zoom: defaultZoom
          })
        }
        afterSourceLoaded((source) => {
          source.setData({
            type: "FeatureCollection",
            features: props.showAll ? pubsToMapFeatures(props.pubs): [],
          });
        });
      }
    }
  }

  const mapContainerStyles = props.fullScreen ?? false ? {
    width: "100%",
    height: "100%",
  } : {};
  return (
    <div
      ref={mapContainer}
      className={`map-container ${props.className}`}
      style={mapContainerStyles}
    />
  );
}

export default Map;
